import React from "react";
import {Tooltip} from "antd";
import {AiExtractionPriceBreakdown, TokenUsage} from "../../domain/VulSourceItem";
import {formatExternalWebSearchBackend} from "../../domain/AiExtractor";
import {formatCreditsFixed, formatDateTime, formatDurationMs} from "../../utils/FormatUtils";
import * as styles from "./AiRunMetrics.module.css";

type StatisticRow = {
    label: string;
    value: string;
};

type Props = {
    timestampLabel?: string;
    timestamp?: Date;
    tokenUsage?: TokenUsage;
    executionTimeMs?: number;
    priceBreakdown?: AiExtractionPriceBreakdown;
    price?: number;
    children?: React.ReactNode;
};

const TOKEN_COUNT_FORMATTER = new Intl.NumberFormat();

export default function AiRunMetrics({
    timestampLabel,
    timestamp,
    tokenUsage,
    executionTimeMs,
    priceBreakdown,
    price,
    children
}: Props) {
    return (
        <div className={styles.metrics}>
            {timestampLabel && <AiRunMetric label={timestampLabel} value={timestamp ? formatDateTime(timestamp) : "-"}/>}
            <AiRunMetric label="Tokens" value={formatTokenCount(tokenUsage?.totalTokens)} details={buildTokenDetailRows(tokenUsage)}/>
            <AiRunMetric label="Time" value={executionTimeMs != null ? formatDurationMs(executionTimeMs) : null}/>
            <AiRunMetric label="Credits" value={formatCreditsValue(priceBreakdown?.totalCost ?? price)} details={buildPriceDetailRows(priceBreakdown)}/>
            {children}
        </div>
    );
}

export function AiRunMetric({label, value, details}: {label: string; value?: React.ReactNode; details?: StatisticRow[]}) {
    if (value == null) {
        return null;
    }

    const metric = (
        <span className={styles.metric}>
            <span className={styles.label}>{label}:</span>
            <span className={styles.value}>{value}</span>
        </span>
    );

    return details ? (
        <Tooltip overlayClassName={styles.tooltipOverlay} title={details.length > 0 ? <DetailTooltipContent rows={details}/> : label}>
            {metric}
        </Tooltip>
    ) : metric;
}

function buildTokenDetailRows(tokenUsage?: TokenUsage): StatisticRow[] {
    return [
        {label: "in", value: formatTokenCount(tokenUsage?.inputTokens)},
        {label: "cached", value: formatTokenCount(tokenUsage?.inputCachedTokens)},
        {label: "cache write", value: formatTokenCount(tokenUsage?.inputCacheWriteTokens)},
        {label: "out", value: formatTokenCount(tokenUsage?.outputTokens)},
        {label: "reasoning", value: formatTokenCount(tokenUsage?.outputReasoningTokens)}
    ].filter((detail): detail is StatisticRow => detail.value !== null);
}

function formatCreditsValue(value?: number): string | null {
    return typeof value === "number" ? formatCreditsFixed(value, 6) : null;
}

function buildPriceDetailRows(priceBreakdown?: AiExtractionPriceBreakdown): StatisticRow[] {
    if (!priceBreakdown) {
        return [];
    }

    return [
        {label: "in", value: formatCreditsValue(priceBreakdown.inputCost)},
        {label: "cached", value: formatCreditsValue(priceBreakdown.cachedInputCost)},
        {label: "cache write", value: formatCreditsValue(priceBreakdown.inputCacheWriteCost)},
        {label: "out", value: formatCreditsValue(priceBreakdown.outputCost)},
        {label: "tools", value: resolveToolsPriceValue(priceBreakdown)}
    ].filter((detail): detail is StatisticRow => detail.value !== null);
}

function resolveToolsPriceValue(priceBreakdown: AiExtractionPriceBreakdown): string | null {
    if ((priceBreakdown.nativeWebSearchToolCallCount ?? 0) > 0) {
        return formatSearchCostWithCalls(
            priceBreakdown.nativeWebSearchCost,
            priceBreakdown.nativeWebSearchToolCallCount,
            "native"
        );
    }

    if ((priceBreakdown.externalWebSearchToolCallCount ?? 0) > 0) {
        return formatSearchCostWithCalls(
            priceBreakdown.externalWebSearchCost,
            priceBreakdown.externalWebSearchToolCallCount,
            priceBreakdown.externalWebSearchBackend
                ? formatExternalWebSearchBackend(priceBreakdown.externalWebSearchBackend)
                : undefined
        );
    }

    return null;
}

function formatSearchCostWithCalls(costValue?: number, callCount?: number, suffix?: string): string | null {
    const cost = formatCreditsValue(costValue);

    if (cost === null) {
        return null;
    }

    const suffixText = suffix ? ` ${suffix}` : "";
    const callCountText = typeof callCount === "number"
        ? ` (${TOKEN_COUNT_FORMATTER.format(callCount)} calls)`
        : "";

    return `${cost}${suffixText}${callCountText}`;
}

function formatTokenCount(value?: number): string | null {
    if (typeof value !== "number" || !Number.isFinite(value)) {
        return null;
    }

    return TOKEN_COUNT_FORMATTER.format(value);
}

function DetailTooltipContent(props: { rows: StatisticRow[] }) {
    return (
        <div className={styles.tooltipGrid}>
            {props.rows.map(row => (
                <span key={row.label} className={styles.tooltipItem}>
                    <span className={styles.tooltipLabel}>{row.label}</span>
                    <span className={styles.tooltipValue}>{row.value}</span>
                </span>
            ))}
        </div>
    );
}
